import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Note, CalendarBlank, FloppyDisk } from "@phosphor-icons/react";
import { upsertLead } from "@/lib/api";

const STATUS_OPTIONS = [
  { value: "not_contacted", label: "Not Contacted" },
  { value: "contacted", label: "Contacted" },
  { value: "proposal_sent", label: "Proposal Sent" },
  { value: "call_back", label: "Call Back" },
];

function toLocalInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // datetime-local expects YYYY-MM-DDTHH:mm
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate(),
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

export function LeadNotesDialog({ open, onOpenChange, business, currentLead, scanContext, onSaved }) {
  const [status, setStatus] = useState("not_contacted");
  const [remark, setRemark] = useState("");
  const [callbackAt, setCallbackAt] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStatus(currentLead?.status || "not_contacted");
    setRemark(currentLead?.remark || "");
    setCallbackAt(toLocalInputValue(currentLead?.callback_at));
  }, [open, currentLead]);

  const placeId = business?.place_id || business?.data_id;
  const canSave = !!placeId && !saving;

  const save = async () => {
    if (!placeId) {
      toast.error("This business has no place_id — cannot save notes.");
      return;
    }
    setSaving(true);
    try {
      const saved = await upsertLead(placeId, {
        business_name: business?.title,
        status,
        remark,
        callback_at: fromLocalInputValue(callbackAt),
        phone: business?.phone || null,
        gbp_url: business?.gbp_url || null,
        address: business?.address || null,
        niche: scanContext?.niche || null,
        area: scanContext?.area || null,
      });
      toast.success("Notes saved");
      onSaved?.(placeId, saved);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save notes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lead-notes-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-lg font-bold text-ink">
            <Note size={18} weight="fill" className="text-electric" />
            Follow-up notes
          </DialogTitle>
          <DialogDescription className="font-sans text-sm text-muted-foreground">
            {business?.title || "Business"} — track your outreach state so it
            survives future rescans.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              Status
            </label>
            <div
              data-testid="lead-status-group"
              className="mt-2 grid grid-cols-2 gap-2"
            >
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`lead-status-${opt.value}`}
                  onClick={() => setStatus(opt.value)}
                  className={`rounded-md border px-3 py-2 text-left font-sans text-sm transition-colors ${
                    status === opt.value
                      ? "border-electric bg-electric-soft font-semibold text-electric"
                      : "border-border bg-white text-ink hover:border-electric/60"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              Remark
            </label>
            <textarea
              data-testid="lead-remark-input"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="e.g. Spoke to manager, call back Friday morning"
              className="mt-1 w-full resize-y rounded-md border border-border bg-white px-3 py-2 font-sans text-sm text-ink placeholder:text-muted-foreground focus:border-electric focus:outline-none focus:ring-2 focus:ring-electric/30"
            />
            <div className="mt-1 flex justify-end font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {remark.length} / 1000
            </div>
          </div>

          <div>
            <label className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              <CalendarBlank size={12} weight="bold" />
              Callback date &amp; time
            </label>
            <input
              type="datetime-local"
              data-testid="lead-callback-input"
              value={callbackAt}
              onChange={(e) => setCallbackAt(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-white px-3 py-2 font-sans text-sm text-ink focus:border-electric focus:outline-none focus:ring-2 focus:ring-electric/30"
            />
            {callbackAt ? (
              <button
                type="button"
                onClick={() => setCallbackAt("")}
                className="mt-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground hover:text-electric"
              >
                clear callback
              </button>
            ) : null}
          </div>
        </div>

        <div className="mt-2 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-md border border-border bg-white px-3 py-2 font-sans text-sm text-ink transition-colors hover:border-electric hover:text-electric"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="lead-save-btn"
            onClick={save}
            disabled={!canSave}
            className="inline-flex items-center gap-1.5 rounded-md bg-electric px-4 py-2 font-sans text-sm font-semibold text-white transition-transform hover:-translate-y-[1px] hover:bg-electric-hover disabled:opacity-70 disabled:hover:translate-y-0"
          >
            <FloppyDisk size={14} weight="bold" />
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_STYLES = {
  not_contacted: {
    label: "Not Contacted",
    bg: "bg-[#F1F3F5]",
    text: "text-[#495057]",
    border: "border-[#DEE2E6]",
    dot: "bg-[#868E96]",
  },
  contacted: {
    label: "Contacted",
    bg: "bg-[#E8ECFF]",
    text: "text-[#002DFF]",
    border: "border-[#B8C4FF]",
    dot: "bg-[#002DFF]",
  },
  proposal_sent: {
    label: "Proposal Sent",
    bg: "bg-[#FEF3C7]",
    text: "text-[#92400E]",
    border: "border-[#FDE68A]",
    dot: "bg-[#B45309]",
  },
  call_back: {
    label: "Call Back",
    bg: "bg-[#EDE0FF]",
    text: "text-[#5B21B6]",
    border: "border-[#D4BBFF]",
    dot: "bg-[#7C3AED]",
  },
};

export function LeadStatusBadge({ status, testid }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.not_contacted;
  return (
    <span
      data-testid={testid || `lead-status-badge-${status}`}
      className={`inline-flex items-center gap-1.5 rounded-full border ${s.bg} ${s.text} ${s.border} px-2.5 py-0.5 text-xs font-medium`}
    >
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

export function callbackFlag(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const now = Date.now();
  const day = 24 * 3600 * 1000;
  if (t < now - 60 * 1000) return "overdue";
  if (t < now + day) return "today";
  return "upcoming";
}
